import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  Paper,
  Typography,
} from '@mui/material';
import { motion } from 'framer-motion';
import API_ENDPOINTS from '../config/apiConfig';
import { useTranslation } from '../i18n/i18n';
import Logo from './Logo';

const PRIVACY_VERSION = '1.0';

function PrivacyConsent({ onAccepted }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [privacyText, setPrivacyText] = useState('');

  useEffect(() => {
    fetch(require('../data/privacy-agreements/Mexico.txt'))
      .then((res) => res.text())
      .then((text) => setPrivacyText(text))
      .catch(() => setPrivacyText(''));
  }, []);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const threshold = 20;
    if (el.scrollHeight - el.scrollTop - el.clientHeight <= threshold) {
      setScrolledToBottom(true);
    }
  }, []);

  const handleAccept = async () => {
    setSubmitting(true);
    setErrorMessage('');
    try {
      const response = await fetch(API_ENDPOINTS.PRIVACY_ACCEPT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ version: PRIVACY_VERSION }),
      });
      if (response.ok) {
        if (onAccepted) onAccepted();
        navigate('/dashboard');
      } else {
        setErrorMessage(t('privacy_accept_error'));
      }
    } catch (error) {
      console.error('Error accepting privacy notice:', error);
      setErrorMessage(t('privacy_accept_error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: { xs: 2, sm: 4 }, mb: 4 }}>
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Paper elevation={6} sx={{ p: { xs: 2, sm: 4 }, borderRadius: '15px' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Logo width={180} />
          </Box>
          <Typography variant="h5" align="center" gutterBottom>
            {t('privacy_title')}
          </Typography>

          <Box
            ref={scrollRef}
            onScroll={handleScroll}
            sx={{
              maxHeight: { xs: '45vh', sm: '50vh' },
              overflowY: 'auto',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
              mb: 2,
              backgroundColor: '#fafafa',
            }}
          >
            {privacyText ? (
              <Typography
                variant="body2"
                sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}
              >
                {privacyText}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t('privacy_loading')}
              </Typography>
            )}
          </Box>

          {!scrolledToBottom && (
            <Typography
              variant="caption"
              color="text.secondary"
              align="center"
              display="block"
              sx={{ mb: 1 }}
            >
              {t('privacy_scroll_hint')}
            </Typography>
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
                disabled={!scrolledToBottom}
                color="primary"
              />
            }
            label={
              <Typography variant="body2">
                {t('privacy_checkbox')}
              </Typography>
            }
            sx={{ mb: 2 }}
          />

          {errorMessage && (
            <Typography color="error" align="center" sx={{ mb: 1 }}>
              {errorMessage}
            </Typography>
          )}

          <Button
            fullWidth
            variant="contained"
            color="primary"
            disabled={!scrolledToBottom || !checked || submitting}
            onClick={handleAccept}
            sx={{ borderRadius: '10px', padding: '10px' }}
          >
            {submitting ? t('please_wait') : t('privacy_accept_button')}
          </Button>
        </Paper>
      </motion.div>
    </Container>
  );
}

export default PrivacyConsent;
